import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import '../assets/Toast.css';

let toastQueue = [];
let toastListeners = [];

// Enhanced addToast function with better API
export const addToast = (messageOrOptions, type = "info") => {
  let toast;

  // Handle both API formats
  if (typeof messageOrOptions === 'object' && messageOrOptions.message) {
    toast = {
      id: Date.now() + Math.random(),
      message: messageOrOptions.message,
      subtitle: messageOrOptions.subtitle,
      type: messageOrOptions.type || "info",
      duration: messageOrOptions.duration || 4000,
      createdAt: Date.now()
    };
  } else {
    toast = {
      id: Date.now() + Math.random(),
      message: String(messageOrOptions),
      type: type || "info",
      duration: 4000,
      createdAt: Date.now()
    };
  }

  // Collapse only callback bursts while the original toast is still visible.
  const duplicateWindowMs = 1500;
  const hasRecentDuplicate = toastQueue.some(existing => (
    existing.type === toast.type
    && existing.message === toast.message
    && toast.createdAt - existing.createdAt < duplicateWindowMs
  ));
  if (hasRecentDuplicate) return;

  toastQueue.push(toast);  // Notify all listeners
  toastListeners.forEach(listener => listener([...toastQueue]));

  // Auto remove after duration
  setTimeout(() => {
    removeToast(toast.id);
  }, toast.duration);
};

const removeToast = (id) => {
  toastQueue = toastQueue.filter(toast => toast.id !== id);
  toastListeners.forEach(listener => listener([...toastQueue]));
};

export default function Toast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const listener = (newToasts) => {
      setToasts(newToasts);
    };

    toastListeners.push(listener);

    return () => {
      toastListeners = toastListeners.filter(l => l !== listener);
    };
  }, []);

  const getToastIcon = (type) => {
    switch (type) {
      case 'success': return <Icon name="check" size={18} />;
      case 'error': return <Icon name="shield" size={18} />;
      case 'warning': return <Icon name="bell" size={18} />;
      default: return <Icon name="bell" size={18} />;
    }
  };
  const stripLeadingEmoji = (text) => {
    if (!text) return '';
    const s = String(text).trim();
    let i = 0;
    // advance over code points that are NOT letters or numbers
    while (i < s.length) {
      const cp = s.codePointAt(i);
      const charLen = cp > 0xffff ? 2 : 1;
      const ch = s.slice(i, i + charLen);
      // If this code point is a letter or number, stop stripping
      try {
        if (/\p{L}|\p{N}/u.test(ch)) break;
      } catch {
        // Fallback: ASCII letter or digit
        if (/\w/.test(ch)) break;
      }
      i += charLen;
    }
    return s.slice(i).trim();
  };

  return (
    <div className="toast-container">
      {toasts.map(toast => {
        const sanitized = stripLeadingEmoji(toast.message);
        return (
          <div key={toast.id} className={`toast ${toast.type || 'info'}`} onClick={() => removeToast(toast.id)}>
            <span className="toast-icon" aria-hidden>{getToastIcon(toast.type)}</span>
            <div className="toast-content">
              <div className="toast-message">{sanitized}</div>
              {toast.subtitle ? <div className="toast-sub">{toast.subtitle}</div> : null}
            </div>
            <button className="toast-close" aria-label="Close" onClick={(e) => { e.stopPropagation(); removeToast(toast.id); }}>
              <Icon name="close" size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
